/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { showNotification } from 'store/actions/notifications';
import { NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'constants/notification';
import {
  paymentRow,
  installmentsInterest,
  PAYMENT_CREDIT_CARD,
  PAYMENT_BANK_SLIP,
  PAYMENT_ITAU_BANK_SLIP,
  PAYMENT_CREDIT_CARD_RECURRENT,
  IUGU_MAX_VALUE,
  getInstallmentInterestsList,
  getInstallmentsInterests,
  PAYMENT_MAXI_PAGO,
  PAYMENT_BARTE,
} from 'constants/sales';
import {
  Paper,
  makeStyles,
} from '@material-ui/core';
import { convertCurrency, stripValue } from 'helpers';
import moment from 'moment';
import { createPayment, removePayment } from 'store/actions/payment';
import { calculateSale } from 'store/actions/sales';
import { prepareToSendPaymentRow, chargable } from './functions';
import PaymentRowAdd from './PaymentRowAdd';
import PaymentRowShow from './PaymentRowShow';

const ADD = 'ADD';
// Para esses métodos o `value` da API é o valor da parcela (total = value * months);
// nos demais, `value` já é o total.
const PER_INSTALLMENT_VALUE_METHODS = [
  PAYMENT_BANK_SLIP,
  PAYMENT_ITAU_BANK_SLIP,
  PAYMENT_CREDIT_CARD_RECURRENT,
];

const PAYMENT_COPY_KEY_PREFIX = 'nova_venda_payment_copy';
const PAYMENT_COPY_TTL_MS = 5 * 60 * 1000; // 5 minutos
const PAYMENT_COPIED_EVENT = 'nova_venda_payment_copied';

function savePaymentCopy(saleId, data) {
  try {
    localStorage.setItem(
      `${PAYMENT_COPY_KEY_PREFIX}_${saleId}`,
      JSON.stringify({ expiresAt: Date.now() + PAYMENT_COPY_TTL_MS, data }),
    );
  } catch (e) {
    // ignore storage errors
  }
}

function loadPaymentCopy(saleId) {
  try {
    const raw = localStorage.getItem(`${PAYMENT_COPY_KEY_PREFIX}_${saleId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.expiresAt || parsed.expiresAt < Date.now()) {
      localStorage.removeItem(`${PAYMENT_COPY_KEY_PREFIX}_${saleId}`);
      return null;
    }
    return parsed.data;
  } catch (e) {
    return null;
  }
}

function clearPaymentCopy(saleId) {
  try {
    localStorage.removeItem(`${PAYMENT_COPY_KEY_PREFIX}_${saleId}`);
  } catch (e) {
    // ignore storage errors
  }
}

const useStyles = makeStyles((theme) => ({
  paper: (props) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(props.mode === ADD ? 3 : 2),
  }),
  select: {
    display: 'flex',
    alignItems: 'center',
  },
  selectPadding: {
    paddingRight: theme.spacing(4),
  },
  hiddenOverflow: {
    overflow: 'hidden',
  },
}));

function PaymentRow({
  row, mode, scope, amountLeft, notification, ...props
}) {
  const classes = useStyles({ mode });
  const formObj = row
    ? { ...paymentRow, ...row, value: parseInt(row.value, 10) / 100 }
    : paymentRow;
  const [form, setForm] = useState(formObj);
  const { total } = scope;
  const checkInterest = (method, months, charge, value) => {
    let chargeInterest = false;
    let interest_total = 0;
    let interest_percentage = 0;
    const payment_method = method;
    const formValue = value && value.includes(',') ? stripValue(value) : value;
    let customer_interest = charge;

    if (chargable(method)) {
      chargeInterest = charge;
      const installment = getInstallmentsInterests(charge, scope.family_plan);
      interest_percentage = installment[parseInt(months, 10) - 1].total;
      // @ts-ignore
      interest_total = parseInt(stripValue(formValue) / 100 * interest_percentage, 10);

      if (method === PAYMENT_MAXI_PAGO || method === PAYMENT_BARTE) {
        if (parseInt(months) > 12) {
          props.calculateSale(scope.id, true);
        } else {
          props.calculateSale(scope.id, false);
        }
      }
    } else {
      props.calculateSale(scope.id, false);
    }
    setForm((prev) => ({
      ...prev,
      payment_method,
      months,
      interest_percentage,
      customer_interest,
      interest_total,
      chargeInterest,
      value,
    }));
  };
  const handleChange = (name, value) => {
    setForm({ ...form, [name]: value });
    if (name === 'months') checkInterest(form.payment_method, value, form.chargeInterest, form.value);
    else if (name === 'chargeInterest') checkInterest(form.payment_method, form.months, value, form.value);
    else if (name === 'value') checkInterest(form.payment_method, form.months, form.chargeInterest, value);
  };
  const handleChangeMethod = (value) => {
    handleChange('payment_method', value);
    setForm({
      ...form,
      payment_method: value,
    });
    checkInterest(value, form.months, form.chargeInterest, form.value);
  };
  const handleCents = () => {
    let newValue = `${form.value}`;
    if (newValue !== '' && !newValue.includes(',')) {
      newValue = `${newValue},00`;
    }
    if (stripValue(newValue) > amountLeft) newValue = convertCurrency(amountLeft, false);
    checkInterest(form.payment_method, form.months, form.chargeInterest, newValue);
  };
  const payerData = () => ({
    name: row.name || '',
    email: row.email || '',
    doc_number: row.doc_number || '',
    phone: row.phone || '',
    address: row.address || '',
  });

  // Botão "Copiar dados": só os dados do responsável, sem remover o pagamento
  const handleCopyPayerData = () => {
    if (!row) return;

    savePaymentCopy(scope.id, payerData());
    window.dispatchEvent(new CustomEvent(PAYMENT_COPIED_EVENT));
    notification(NOTIFICATION_TYPE_SUCCESS, 'Dados do responsável copiados para o novo pagamento.');
  };

  // Botão "Copiar e Remover": cópia completa do pagamento; a remoção acontece no submit do form
  const handleCopyPaymentData = () => {
    if (!row) return;

    const months = row.months || 1;
    // Normaliza para o valor total, que é o que o form de adicionar espera
    const totalValue = PER_INSTALLMENT_VALUE_METHODS.includes(row.payment_method)
      ? row.value * months
      : row.value;
    const data = {
      ...payerData(),
      value: totalValue,
      // A remoção é assíncrona: quando o form ADD já está visível, o amountLeft
      // atual ainda não inclui este pagamento. O clamp deve usar o amountLeft futuro.
      maxValue: amountLeft + totalValue,
      payment_method: row.payment_method,
      months,
      chargeInterest: Boolean(row.customer_interest),
    };
    if (row.due_date) data.due_date = moment(row.due_date).toISOString();
    if (row.entry_percentage) data.entry_percentage = row.entry_percentage;

    savePaymentCopy(scope.id, data);
    window.dispatchEvent(new CustomEvent(PAYMENT_COPIED_EVENT));
  };

  useEffect(() => {
    if (mode !== ADD) return;

    const applyCopiedData = () => {
      const data = loadPaymentCopy(scope.id);
      if (!data) return;

      if (data.payment_method) {
        // Cópia completa ("Copiar e Remover"): restaura também valor, método, parcelas e vencimento
        const { maxValue, ...paymentData } = data;
        const limit = maxValue != null ? maxValue : amountLeft;
        const copiedValue = parseInt(paymentData.value, 10) || 0;
        const safeValue = copiedValue > limit ? limit : copiedValue;
        const nextValue = safeValue > 0 ? convertCurrency(safeValue, false) : '';

        setForm((prev) => ({
          ...prev,
          ...paymentData,
          due_date: paymentData.due_date ? moment(paymentData.due_date).toDate() : prev.due_date,
          value: nextValue,
        }));
        checkInterest(
          paymentData.payment_method, paymentData.months, paymentData.chargeInterest, nextValue,
        );
      } else {
        // Só dados do responsável ("Copiar dados")
        setForm((prev) => ({ ...prev, ...data }));
      }

      clearPaymentCopy(scope.id);
    };

    applyCopiedData();
    window.addEventListener(PAYMENT_COPIED_EVENT, applyCopiedData);
    return () => window.removeEventListener(PAYMENT_COPIED_EVENT, applyCopiedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, amountLeft]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === ADD) {
      if (
        (form.payment_method === PAYMENT_CREDIT_CARD || form.payment_method === PAYMENT_BANK_SLIP)
        && stripValue(form.value) > IUGU_MAX_VALUE
      ) {
        notification(NOTIFICATION_TYPE_ERROR, `Limite de ${convertCurrency(IUGU_MAX_VALUE)} para este tipo de pagamento.`);
      } else {
        props.createPayment(scope.id, prepareToSendPaymentRow(form), scope.id);
        setForm(formObj);
      }
    } else props.removePayment(row.blockchain, 'Editado no escopo de venda', scope.id);
  };
  const setValueAsTotal = () => {
    const value = stripValue(form.value) === amountLeft ? '' : convertCurrency(amountLeft, false);
    checkInterest(form.payment_method, form.months, form.chargeInterest, value);
  };
  return (
    <Paper elevation={3} className={classes.paper}>
      <form onSubmit={handleSubmit}>
        {mode === ADD
          ? (
            <PaymentRowAdd
              amountLeft={amountLeft}
              form={form}
              total={total}
              classes={classes}
              setValueAsTotal={setValueAsTotal}
              familyPlan={scope.family_plan}
              handleCents={handleCents}
              handleChange={handleChange}
              handleChangeMethod={handleChangeMethod}
            />
          ) : (
            <PaymentRowShow
              row={row}
              form={form}
              installments={scope.payments.filter(p => p.blockchain === row.blockchain)}
              canCopyData={amountLeft > 0}
              onCopyPayerData={handleCopyPayerData}
              onCopyPaymentData={handleCopyPaymentData}
            />
          )}
      </form>
    </Paper>
  );
}

const mapStateToProps = (state) => ({
  paymentStatus: state.payment.status,
});

export default connect(mapStateToProps, { createPayment, removePayment, notification: showNotification, calculateSale })(PaymentRow);
